import React, {Fragment, useEffect, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, user} from '../../../store/reducers/user';
import {Grid, Paper} from '@mui/material';
import moment from 'moment';
import MessageBox from '../../../util/messageBox';
import FormLabel from '@mui/material/FormLabel';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import Typography from '@mui/material/Typography';
import useOpenState from '../../../hooks/useOpenState';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {setSnackbar} from '../../../store/reducers/globals';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#313233',
        padding: 10,
        marginBottom: 10,
    },
    button: {
        background: '#fff',
        color: '#000',
        '&:hover': {
            background: '#fff',
        },
    },
    clinicalCourseAuthor: {
        float: 'right',
        fontSize: 'small',
        fontStyle: 'italic',
    },
    outcome: {
        borderRadius: '7px',
        border: '1px solid #7199E6',
        background: '#cfdffd',
        padding: 10,
        color: '#3F73D6',
        marginBottom: '10px',
    },
    paper: {
        background: theme.palette.background.paper,
        padding: '0 10px 10px 10px',
    },
    medical: {
        margin: 5,
    },
    select: {
        float: 'right',
        width: 150,
        marginBottom: 5,
    },
    accordion: {
        marginTop: 20,
        backgroundColor: theme.palette.accordion.backgroundColor,
    },
    heading: {
        fontSize: '20px',
    },
    error: {
        color: '#f44336',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    label: {
        color: theme.palette.mode === 'light' ? '#626466' : '#FFFFFF',
        fontWeight: 'bold',
        fontSize: 14,
    },
}));

const EdauDischarge = (props) => {
    const {t} = useTranslation();
    const classesLocal = useStyles();
    const dispatch = useDispatch();

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const [isOpenMessageOpen, openOpenMessage, closeOpenMessage] = useOpenState(false, true);

    const [form, setForm] = useState({
        option: null,
        discharged: '',
        transferred: '',
        other: '',
        analgesia: null,
        cannula: null,
        carerNok: null,
        eds: null,
        homeAccess: null,
        leaflets: null,
        mobility: null,
        safeHomeEnv: null,
        transport: null,
        transportDescription: '',
        tto: null,
    });

    useEffect(() => {
        if (props.patient?.studies?.dischargeChecklist) {
            setForm({
                ...props.patient?.studies?.dischargeChecklist,
            });
        }
    }, []);

    const getNote = () => {
        return Notes.getEDAUDischargeNote(form);
    };

    const submit = () => {
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_ONLY_DISCHARGE_EDAU))) {
            const dateOfForm = moment().toISOString();
            const data = {
                ...form,
                when: dateOfForm,
                by: tester,
                login: userObject?.userName,
            };

            const dataToSave = [
                {
                    name: 'studies.dischargeChecklist',
                    value: data,
                    noteToAdd: getNote(),
                },
                {
                    name: 'metadata.EDAUDischarged',
                    value: true,
                },
            ];
            if (props._customOnSave) {
                props._customOnSave(dataToSave);
            } else if (props.onChange) {
                props.onChange(dataToSave);
                props.close();
            }
        } else {
            dispatch(setSnackbar({type: 'warning', message: t('User has no permission to discharge the patient.')}));
        }
    };

    useEffect(() => {
        if (props.onCustomButtons) {
            // props.onCustomButtons();
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(() => {
                openOpenMessage();
                return false;
            });
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(!dispatch(hasPermission(Permissions.AGYLE_CAN_ONLY_DISCHARGE_EDAU)));
        }

        if (props.onCustomSubmitText) {
            props.onCustomSubmitText('Discharge');
        }

        if (props.agyleDialogInsertButtons) {
            const buttons = [];

            if (!props.disableSave) {
                buttons.push(
                    <AgyleButton
                        key="discahrge"
                        variant="contained"
                        color="primary"
                        onClick={openOpenMessage}
                        disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_ONLY_DISCHARGE_EDAU))}
                    >
                        {t('Discharge')}
                    </AgyleButton>,
                );
            }
            props.agyleDialogInsertButtons(buttons);
        }
    }, [props.disableSave, props.agyleDialogInsertButtons]);

    const handelChangeOption = (val) => {
        setForm(prevState => ({
            ...prevState,
            option: val,
        }));
    };

    const renderOption = (type, label) => {
        return (
            <Grid item xs={12}>
                <div className={classesLocal.divRow}>
                    <AgyleBlockSwitch
                        onChange={event => setForm(prevState => ({
                            ...prevState,
                            [type]: event,
                        }))}
                        value={form[type]}
                        valueObject={{
                            true: 'yes',
                            false: 'no',
                            null: 'notApplicable',
                        }}
                        customLabels={{
                            empty: t('N/A'),
                        }}
                        nullState
                    />

                    <FormLabel className={classesLocal.label} style={{marginLeft: 10}}>
                        {t(label)}
                    </FormLabel>
                </div>
            </Grid>
        );
    };

    return (
        <>
            <Paper className={classesLocal.root}>
                <Grid container direction="row" spacing={1} alignItems="center">
                    <Grid item xs={12}>
                        <FormLabel className={classesLocal.label}>
                            {t('Option')}
                        </FormLabel>

                        <AgyleBlockSwitch
                            onChange={handelChangeOption}
                            value={form.option}
                            nullState
                            customLabels={{
                                yes: t('Discharged'),
                                no: t('Other'),
                                empty: t('Transferred to a ward'),
                            }}
                            valueObject={{
                                true: 'discharged',
                                false: 'other',
                                'null': 'transferred',
                            }}
                        />
                    </Grid>

                    {form.option === 'discharged' &&
                        <Grid item xs={12}>
                            <AgyleTextField
                                label={t('Discharged')}
                                onChange={event => setForm(prevState => ({
                                    ...prevState,
                                    discharged: event.target.value,
                                }))}
                                value={form.discharged}
                                debounce
                                fullWidth
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    }

                    {form.option === 'transferred' &&
                        <Grid item xs={12}>
                            <AgyleTextField
                                label={t('Transferred to a ward')}
                                onChange={event => setForm(prevState => ({
                                    ...prevState,
                                    transferred: event.target.value,
                                }))}
                                value={form.transferred}
                                debounce
                                fullWidth
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    }

                    {form.option === 'other' &&
                        <Grid item xs={12}>
                            <AgyleTextField
                                label={t('Other')}
                                onChange={event => setForm(prevState => ({
                                    ...prevState,
                                    other: event.target.value,
                                }))}
                                value={form.other}
                                debounce
                                fullWidth
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    }
                </Grid>
            </Paper>

            <Paper className={classesLocal.root}>
                <Grid container direction="row" spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <Typography variant="h5" fontWeight="bold">
                            {t('Medical Discharge Checklist')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <FormLabel className={classesLocal.label}>
                            {t('Please confirm that each action has been completed')}
                        </FormLabel>
                    </Grid>

                    {renderOption('safeHomeEnv', 'Ensure safe home environment')}
                    {renderOption('mobility', 'Ensure mobility is adequate for discharge')}
                    {renderOption('analgesia', 'Analgesia/TTOs have been prescribed')}
                    {renderOption('eds', 'EDS has been completed')}
                    {renderOption('leaflets', 'Advice leaflets given')}
                </Grid>
            </Paper>

            <Paper className={classesLocal.root}>
                <Grid container direction="row" spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <Typography variant="h5" fontWeight="bold">
                            {t('Instructions for Discharging Staff Member')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <FormLabel className={classesLocal.label}>
                            {t('Please confirm that each action has been completed')}
                        </FormLabel>
                    </Grid>

                    {renderOption('cannula', 'Cannula has been removed')}
                    {renderOption('transport', 'Transport arranged')}

                    {form?.transport === 'yes' &&
                        <Grid item xs={12}>
                            <AgyleTextField
                                label={t('Hospital Transport Booking Reference')}
                                value={form.transportDescription || ''}
                                onChange={event => setForm(prevState => ({
                                    ...prevState,
                                    transportDescription: event.target.value,
                                }))}
                                debounce
                                fullWidth
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    }

                    {renderOption('tto', 'Patient has received all TTOs')}
                    {renderOption('homeAccess', 'Patient has access to home')}
                    {renderOption('carerNok', 'Carer or Next of Kin informed of discharge')}
                </Grid>
            </Paper>

            <MessageBox
                open={isOpenMessageOpen}
                onClose={closeOpenMessage}
                title={t('Confirmation')}
                message={t('Are you sure you want to save?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={submit}
            />
        </>
    );

};

export default EdauDischarge;